/**
 * Analytics Utility Functions
 *
 * Centralized analytics tracking for Meta Pixel (Facebook Ads) and Google Analytics 4.
 * Provides type-safe wrappers around the global tracking functions.
 */

// Extend Window interface to include analytics globals
declare global {
  interface Window {
    fbq?: (command: string, eventName: string, params?: Record<string, any>) => void;
    gtag?: (command: string, targetId: string, config?: Record<string, any>) => void;
  }
}

/**
 * Track an event with both Meta Pixel and GA4
 *
 * @param eventName - Name of the event to track
 * @param eventParams - Optional parameters to include with the event
 */
export const trackEvent = (eventName: string, eventParams?: Record<string, any>) => {
  // Track with Meta Pixel (Facebook Ads)
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', eventName, eventParams);
  }

  // Track with Google Analytics 4
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, {
      event_category: 'engagement',
      ...eventParams
    });
  }
};

/**
 * Demo-specific event tracking functions
 * These align with the conversion rate optimization plan
 */
export const demoAnalytics = {
  /**
   * Track when a user starts the demo (first question displayed)
   */
  trackDemoStarted: () => {
    trackEvent('demo_started', {
      event_label: 'demo_interaction',
      value: 1
    });
  },

  /**
   * Track when a user completes a specific question
   *
   * @param questionNumber - The question number (1-indexed)
   * @param totalQuestions - Total number of questions in demo
   */
  trackQuestionCompleted: (questionNumber: number, totalQuestions: number) => {
    trackEvent(`demo_question_${questionNumber}_completed`, {
      event_label: 'demo_progress',
      question_number: questionNumber,
      total_questions: totalQuestions,
      progress_percentage: (questionNumber / totalQuestions) * 100
    });
  },

  /**
   * Track when a user completes all demo questions
   *
   * @param score - Number of correct answers
   * @param total - Total number of questions
   */
  trackDemoCompleted: (score: number, total: number) => {
    const percentage = Math.round((score / total) * 100);

    trackEvent('demo_completed', {
      event_label: 'demo_completion',
      score: score,
      total: total,
      percentage: percentage,
      value: percentage // Use percentage as conversion value
    });

    // Also track as Facebook 'Lead' since completing demo is a strong lead signal
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', 'Lead', {
        content_name: 'Demo Completion',
        value: percentage,
        currency: 'AUD'
      });
    }
  },

  /**
   * Track when a user abandons the demo before completion
   *
   * @param questionsAnswered - Number of questions answered before abandoning
   * @param totalQuestions - Total number of questions in demo
   */
  trackDemoAbandoned: (questionsAnswered: number, totalQuestions: number) => {
    trackEvent('demo_abandoned', {
      event_label: 'demo_dropout',
      questions_answered: questionsAnswered,
      total_questions: totalQuestions,
      abandonment_point: questionsAnswered + 1 // Which question they abandoned on
    });
  },

  /**
   * Track when a user clicks signup after completing the demo
   * This is a critical conversion metric
   */
  trackSignupAfterDemo: () => {
    trackEvent('signup_after_demo', {
      event_label: 'demo_conversion',
      value: 100 // High value since this is our primary conversion goal
    });

    // Track as Facebook 'CompleteRegistration' intent
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', 'InitiateCheckout', {
        content_name: 'Signup After Demo',
        value: 4.99,
        currency: 'AUD'
      });
    }
  }
};
